import { useEffect, useState } from 'react';
import { X, UserPlus, AlertTriangle } from 'lucide-react';
import { checkCrmLeadDuplicate, createCrmLead, type CrmLeadCard } from '../../../api';
import { AdminOverlayPortal } from '../AdminOverlayPortal';

type Props = {
  onClose: () => void;
  onCreated: () => void;
  onOpenExisting?: (leadId: number) => void;
  hapticSelection: () => void;
  hapticSuccess: () => void;
};

export function CrmNewLeadModal({ onClose, onCreated, onOpenExisting, hapticSelection, hapticSuccess }: Props) {
  const [title, setTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [telegramId, setTelegramId] = useState('');
  const [notes, setNotes] = useState('');
  const [taskTomorrow, setTaskTomorrow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState<CrmLeadCard[]>([]);

  useEffect(() => {
    if (phone.trim().length < 6 && !telegramId.trim()) {
      setDupes([]);
      return;
    }
    const t = setTimeout(() => {
      void checkCrmLeadDuplicate({
        phone: phone.trim() || undefined,
        telegram_id: telegramId.trim() ? Number(telegramId.trim()) : undefined,
      }).then((r) => setDupes(r.matches || []));
    }, 400);
    return () => clearTimeout(t);
  }, [phone, telegramId]);

  const submit = async () => {
    if (!title.trim()) return;
    hapticSelection();
    setSaving(true);
    try {
      await createCrmLead({
        title: title.trim(),
        phone: phone.trim() || undefined,
        telegram_id: telegramId.trim() ? Number(telegramId.trim()) : undefined,
        notes: notes.trim() || undefined,
        create_task_tomorrow: taskTomorrow,
      });
      hapticSuccess();
      onCreated();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminOverlayPortal zIndex={90}>
      <div className="flex-1 flex items-end justify-center min-h-0">
        <div
          className="w-full max-w-lg rounded-t-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto"
          style={{ background: '#0F2340', borderTop: '1px solid var(--border-subtle)' }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
                Новый клиент с улицы
              </p>
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-3)' }}>
                Лид сразу на вас · дальше замер или «Оформить заказ»
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="press p-2 rounded-full min-h-[44px] min-w-[44px] touch-manipulation"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              <X size={18} color="var(--text-2)" />
            </button>
          </div>
          {dupes.length > 0 && (
            <div
              className="p-3 rounded-xl space-y-2"
              style={{ background: 'rgba(246,173,85,0.1)', border: '1px solid rgba(246,173,85,0.35)' }}
            >
              <p className="text-xs font-bold flex items-center gap-1" style={{ color: '#F6AD55' }}>
                <AlertTriangle size={14} /> Похожий лид уже есть
              </p>
              {dupes.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => {
                    hapticSelection();
                    onOpenExisting?.(d.id);
                    onClose();
                  }}
                  className="press w-full text-left text-xs py-2 px-2 rounded-lg touch-manipulation min-h-[40px]"
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-1)' }}
                >
                  {d.title} · {d.source}
                </button>
              ))}
            </div>
          )}
          <input
            className="input-glass w-full text-sm min-h-[44px] touch-manipulation"
            placeholder="Имя / компания *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <input
            className="input-glass w-full text-sm min-h-[44px] touch-manipulation"
            placeholder="Телефон (для звонка и замера)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <input
            className="input-glass w-full text-sm min-h-[44px] touch-manipulation"
            placeholder="Telegram ID (если есть)"
            value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
          />
          <textarea
            className="input-glass w-full text-sm min-h-[72px] resize-none touch-manipulation"
            placeholder="Предварительный комментарий: что хочет, бюджет, объект…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <label
            className="flex items-center gap-2 text-xs min-h-[44px] touch-manipulation"
            style={{ color: 'var(--text-2)' }}
          >
            <input type="checkbox" checked={taskTomorrow} onChange={(e) => setTaskTomorrow(e.target.checked)} />
            Создать задачу «Позвонить» на завтра
          </label>
          <button
            type="button"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
            className="press w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold min-h-[48px] touch-manipulation"
            style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }}
          >
            <UserPlus size={16} /> {saving ? '…' : 'Создать лид'}
          </button>
        </div>
      </div>
    </AdminOverlayPortal>
  );
}
